import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { 
  Rocket, 
  Lock, 
  CheckCircle, 
  Video, 
  Scissors, 
  Calendar, 
  MessageCircle,
  Zap,
  TrendingUp,
  Shield
} from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL;

export default function AutopilotSettings() {
  const [progress, setProgress] = useState(null);
  const [unlockStatus, setUnlockStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [canUse, setCanUse] = useState(null);
  const [allowedModules, setAllowedModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadAutopilotData();
  }, []);

  const loadAutopilotData = async () => {
    try {
      // Load progress
      const progressRes = await fetch(`${BACKEND_URL}/api/autopilot/progress`, {
        credentials: 'include'
      });
      const progressData = await progressRes.json();
      setProgress(progressData.progress);
      setUnlockStatus(progressData.unlock_status);

      // Load settings
      const settingsRes = await fetch(`${BACKEND_URL}/api/autopilot/settings`, {
        credentials: 'include'
      });
      const settingsData = await settingsRes.json();
      setSettings(settingsData.settings);
      setCanUse(settingsData.can_use_autopilot);
      setAllowedModules(settingsData.allowed_modules);
    } catch (error) {
      console.error('Error loading autopilot data:', error);
      setMessage({ type: 'error', text: 'Failed to load autopilot data' });
    }
  };

  const updateSettings = async (updates) => {
    setLoading(true);
    try {
      const params = new URLSearchParams(updates);
      const res = await fetch(`${BACKEND_URL}/api/autopilot/settings?${params}`, {
        method: 'PUT',
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.success) {
        setMessage({ type: 'success', text: 'Settings updated successfully!' });
        await loadAutopilotData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update settings' });
      console.error('Error updating settings:', error);
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const getProgressPercent = () => {
    if (!progress) return 0;
    
    const livesProgress = (progress.lives_count / unlockStatus.thresholds.lives) * 100;
    const clipsProgress = (progress.clips_count / unlockStatus.thresholds.clips) * 100;
    const schedulesProgress = (progress.schedules_count / unlockStatus.thresholds.schedules) * 100;
    
    return Math.max(livesProgress, clipsProgress, schedulesProgress);
  };

  const isModuleAllowed = (module) => {
    return allowedModules.includes(module);
  };

  const renderUnlockProgress = () => {
    if (!progress || !unlockStatus) return null;

    if (unlockStatus.is_unlocked) {
      return (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <div className="font-semibold text-lg text-green-900">
                  Autopilot Unlocked! 🎉
                </div>
                <div className="text-sm text-green-700">
                  You've completed the required manual approvals. Autopilot is ready to use!
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="border-orange-200 bg-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-orange-600" />
            Unlock Autopilot
          </CardTitle>
          <CardDescription>
            Complete manual actions to ensure safe automation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span>Progress to unlock</span>
              <span className="font-semibold">{Math.round(getProgressPercent())}%</span>
            </div>
            <Progress value={getProgressPercent()} className="h-2" />
          </div>

          <div className="grid gap-3">
            <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
              <div className="flex items-center gap-2">
                <Video className="h-4 w-4 text-blue-500" />
                <span className="text-sm">Manually approved lives</span>
              </div>
              <Badge variant={progress.lives_count >= unlockStatus.thresholds.lives ? "default" : "outline"}>
                {progress.lives_count} / {unlockStatus.thresholds.lives}
              </Badge>
            </div>

            <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
              <div className="flex items-center gap-2">
                <Scissors className="h-4 w-4 text-purple-500" />
                <span className="text-sm">Manually approved clips</span>
              </div>
              <Badge variant={progress.clips_count >= unlockStatus.thresholds.clips ? "default" : "outline"}>
                {progress.clips_count} / {unlockStatus.thresholds.clips}
              </Badge>
            </div>

            <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-green-500" />
                <span className="text-sm">Manually scheduled posts</span>
              </div>
              <Badge variant={progress.schedules_count >= unlockStatus.thresholds.schedules ? "default" : "outline"}>
                {progress.schedules_count} / {unlockStatus.thresholds.schedules}
              </Badge>
            </div>
          </div>

          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              {unlockStatus.message}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  };

  const renderPlanGate = () => {
    if (!canUse || canUse.can_use) return null;

    if (canUse.reason === 'plan_limitation') {
      return (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-8 w-8 text-blue-600" />
                <div>
                  <div className="font-semibold text-lg text-blue-900">
                    Upgrade to Access Autopilot
                  </div>
                  <div className="text-sm text-blue-700">
                    Autopilot is available on Pro ($29/mo) and Enterprise ($99/mo) plans
                  </div>
                </div>
              </div>
              <Button onClick={() => window.location.href = '/billing'}>
                Upgrade Now
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  const renderModuleToggle = (module, icon, title, description, badge = null) => {
    const isAllowed = isModuleAllowed(module);
    const isEnabled = settings?.[`${module}_autopilot_enabled`] || false;
    const isLocked = !unlockStatus?.is_unlocked || !isAllowed;

    return (
      <div className={`p-4 border rounded-lg ${isLocked ? 'bg-gray-50' : 'bg-white'}`}>
        <div className="flex items-start justify-between">
          <div className="flex gap-3 flex-1">
            <div className={`mt-1 ${isLocked ? 'opacity-50' : ''}`}>
              {icon}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold">{title}</span>
                {badge && <Badge variant="outline" className="text-xs">{badge}</Badge>}
                {isLocked && !isAllowed && (
                  <Badge variant="outline" className="text-xs bg-blue-50">
                    <Lock className="h-3 w-3 mr-1" />
                    Upgrade Required
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
          <Switch
            checked={isEnabled}
            disabled={isLocked || loading}
            onCheckedChange={(checked) => {
              if (!isLocked) {
                updateSettings({ [`${module}_autopilot_enabled`]: checked });
              }
            }}
          />
        </div>
      </div>
    );
  };

  if (!progress || !settings) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading autopilot settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Autopilot Settings</h1>
        <p className="text-muted-foreground">
          Automate your content workflow with AI-powered autopilot
        </p>
      </div>

      {message && (
        <Alert className={`mb-4 ${message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* Unlock Progress */}
        {renderUnlockProgress()}

        {/* Plan Gate */}
        {renderPlanGate()}

        {/* Global Autopilot Switch */}
        {unlockStatus?.is_unlocked && canUse?.can_use && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-orange-500" />
                Full Autopilot
              </CardTitle>
              <CardDescription>
                Master switch to enable all autopilot modules at once
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-2 border-orange-200">
                <div className="flex items-center gap-3">
                  <Rocket className="h-8 w-8 text-orange-600" />
                  <div>
                    <div className="font-semibold text-lg">
                      {settings.global_autopilot_enabled ? 'Full Autopilot Active' : 'Full Autopilot'}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {settings.global_autopilot_enabled 
                        ? 'All allowed modules are running automatically' 
                        : 'Enable all autopilot modules with one switch'}
                    </div>
                  </div>
                </div>
                <Switch
                  checked={settings.global_autopilot_enabled}
                  disabled={loading}
                  onCheckedChange={(checked) => {
                    updateSettings({ global_autopilot_enabled: checked });
                  }}
                  className="data-[state=checked]:bg-orange-500"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Per-Module Toggles */}
        {unlockStatus?.is_unlocked && (
          <Card>
            <CardHeader>
              <CardTitle>Autopilot Modules</CardTitle>
              <CardDescription>
                Control which parts of your workflow are automated
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {renderModuleToggle(
                'live',
                <Video className="h-5 w-5 text-blue-500" />,
                'Live Autopilot',
                'Automatically process and save live stream recordings for clipping'
              )}

              {renderModuleToggle(
                'clip',
                <Scissors className="h-5 w-5 text-purple-500" />,
                'Clip Autopilot',
                'Automatically generate clips from long-form content using AI'
              )}

              {renderModuleToggle(
                'scheduler',
                <Calendar className="h-5 w-5 text-green-500" />,
                'Scheduler Autopilot',
                'Automatically schedule and post your content to platforms'
              )}

              {renderModuleToggle(
                'community',
                <MessageCircle className="h-5 w-5 text-pink-500" />,
                'Community Autopilot',
                'Automatically reply to comments and engage with your audience',
                'Enterprise'
              )}

              {!isModuleAllowed('community') && (
                <Alert className="mt-4">
                  <TrendingUp className="h-4 w-4" />
                  <AlertDescription>
                    Community Autopilot is available on Enterprise plans ($99/mo).
                    <Button 
                      variant="link" 
                      className="p-0 h-auto ml-1"
                      onClick={() => window.location.href = '/billing'}
                    >
                      Upgrade to unlock
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Safety Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-500" />
              Safety & Control
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <div className="flex gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
              <p>
                <strong>You're always in control.</strong> Autopilot only activates after you've demonstrated safe manual usage.
              </p>
            </div>
            <div className="flex gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
              <p>
                <strong>Review before posting.</strong> You can review and approve all automated actions before they go live.
              </p>
            </div>
            <div className="flex gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
              <p>
                <strong>Plan-based limits.</strong> Autopilot respects your plan's content limits and posting caps.
              </p>
            </div>
            <div className="flex gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
              <p>
                <strong>Turn off anytime.</strong> Disable autopilot globally or per-module at any time.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
